import React from 'react';
import {EnumIconType, LayerGeometryType} from '../../api';
import './IkonPreview.scss';
import {useAppConfig} from '@mc/react-app-base';

const DEFAULT_LABEL_FONT_FAMILY = 'Calibri,sans-serif';

type Props = {
    typGeometrie: LayerGeometryType;
    barva: string;
    ikonaText?: string;
    ikona?: string;
    typMarker?: EnumIconType;
    labelEnabled?: boolean;
    labelText?: string;
    labelFontSize?: string;
    labelFontFamily?: string;
    labelFontBold?: boolean;
    labelFontColor?: string;
    labelStrokeColor?: string;
    labelOffsetX?: number;
    labelOffsetY?: number;
};
export const IkonPreview: React.FC<Props> = ({
    typGeometrie,
    barva,
    ikonaText,
    ikona,
    typMarker,
    labelEnabled,
    labelText,
    labelFontSize,
    labelFontFamily,
    labelFontBold,
    labelFontColor,
    labelStrokeColor,
    labelOffsetX,
    labelOffsetY,
}) => {
    const appConfig = useAppConfig();

    const resolveHexColor = (color?: string, fallback = '#373737') => {
        if (!color || color === 'null') {
            return fallback;
        }
        return color.startsWith('#') ? color : `#${color}`;
    };

    const getLabelAnchor = () => {
        switch (typGeometrie) {
            case LayerGeometryType.POINT:
                return {x: 85, y: 85};
            case LayerGeometryType.LINE:
                return {x: 70, y: 88};
            case LayerGeometryType.POLYGON:
                return {x: 85, y: 90};
            default:
                return {x: 85, y: 85};
        }
    };

    const resolveFontSize = (size?: string) => {
        if (!size) {
            return '20px';
        }
        return size.endsWith('px') ? size : `${size}px`;
    };

    const resolveFontFamily = (family?: string) => {
        return family ? `${family},${DEFAULT_LABEL_FONT_FAMILY}` : DEFAULT_LABEL_FONT_FAMILY;
    };

    const renderMap = () => {
        return <image href={appConfig.serverContextPath + '/api/files/img/map_preview.png'} height="165" width="165" />;
    };

    const renderPoint = () => {
        return (
            <>
                <circle cx="85" cy="85" r="20" stroke={'white'} strokeWidth={'3px'} fill={`#${typMarker === EnumIconType.CUSTOM ? barva : 'ffffff00'}`} />
                {
                    {
                        [EnumIconType.CUSTOM]: (
                            <text x="85" y="87" stroke={'white'} dominantBaseline="middle" textAnchor="middle">
                                {ikonaText}
                            </text>
                        ),
                        [EnumIconType.ICON]: (
                            <image
                                href={ikona ? `${appConfig.serverContextPath}/api/files/img/${ikona.replace('files/img/', '')}` : ''}
                                x="70"
                                y="70"
                                height="30"
                                width="30"
                            />
                        ),
                    }[typMarker]
                }
            </>
        );
    };

    const renderLine = () => {
        return <path stroke={`#${barva}`} strokeWidth={'3px'} fill={`#${barva}`} fillOpacity={'30%'} d="M 25,105  115,70 z" />;
    };

    const renderPolygon = () => {
        return <path stroke={`#${barva}`} strokeWidth={'3px'} fill={`#${barva}`} fillOpacity={'30%'} d="M 25,90 65,120 115,115 140,75 80,50 z" />;
    };

    const renderLabel = () => {
        if (!labelEnabled) {
            return null;
        }

        const {x, y} = getLabelAnchor();
        const text = labelText || 'Popisek';
        const fontSize = resolveFontSize(labelFontSize);
        const fontFamily = resolveFontFamily(labelFontFamily);
        const fontWeight = labelFontBold ? '700' : '400';
        const fill = resolveHexColor(labelFontColor, resolveHexColor(barva));
        const hasStroke = !!labelStrokeColor && labelStrokeColor !== 'null';
        const stroke = hasStroke ? resolveHexColor(labelStrokeColor) : undefined;
        const dx = Number.isFinite(labelOffsetX) ? labelOffsetX : 0;
        const dy = Number.isFinite(labelOffsetY) ? labelOffsetY : 0;

        return (
            <text
                x={x + dx}
                y={y + dy}
                fill={fill}
                stroke={stroke}
                strokeWidth={2}
                paintOrder="stroke"
                fontSize={fontSize}
                fontFamily={fontFamily}
                fontWeight={fontWeight}
                dominantBaseline="middle"
                textAnchor="middle"
            >
                {text}
            </text>
        );
    };

    return (
        <div className={'ikon-map-preview'}>
            <svg xmlns="http://www.w3.org/2000/svg" width="165" height="165" id="svg2" version="1.1">
                {renderMap()}

                {{[LayerGeometryType.POINT]: renderPoint(), [LayerGeometryType.LINE]: renderLine(), [LayerGeometryType.POLYGON]: renderPolygon()}[typGeometrie]}
                {renderLabel()}
            </svg>
        </div>
    );
};
